import http from "./http";
import { DefaultModel } from "../models/IModel";

export interface SettlementCycle {
	id: number;
	userId: number;
	code: string;
	periodMonth: number;
	periodYear: number;
	status: number;
	startDate: string;
	endDate: string;
	sentAt: string | null;
	closeAt: string | null;
	paidAt: string | null;
	totalIncome: number;
	country?: string;
	currency?: string;
	createdAt: string;
	updatedAt: string;
	user: {
		id: number;
		fullName: string;
		email: string;
	};
	items: {
		id: number;
		status: number;
	}[];
}

export interface SettlementCyclesResponse {
	docs: SettlementCycle[];
	pages: number;
	total: number;
	currentPage: number;
}

export interface SettlementCyclesParams {
	page?: number;
	perPage?: number;
	search?: string;
	status?: number;
	destinationId?: string;
	periodMonth?: number;
	periodYear?: number;
	partnerId?: number;
	sort?: {
		period?: "ASC" | "DESC";
		status?: "ASC" | "DESC";
		bookingCount?: "ASC" | "DESC";
		totalIncome?: "ASC" | "DESC";
	};
}

export async function getSettlementCycles(
	page: number = 1,
	perPage: number = 20,
	search?: string,
	status?: number,
	sortBy?: string,
	sortOrder?: string,
	area?: string,
	periodMonth?: number,
	periodYear?: number
): Promise<SettlementCyclesResponse> {
	const params: any = {
		page: page,
		perPage: perPage,
	};

	if (search) params.search = search;
	if (status !== null && status !== undefined) params.status = status;
	if (area && area !== "all") params.destinationId = area;
	if (periodMonth !== null && periodMonth !== undefined) params.periodMonth = periodMonth;
	if (periodYear !== null && periodYear !== undefined) params.periodYear = periodYear;

	const queryParts: string[] = [];

	Object.entries(params).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			queryParts.push(`${key}=${encodeURIComponent(String(value))}`);
		}
	});

	if (sortBy && sortOrder) {
		queryParts.push(`sort[${sortBy}]=${sortOrder}`);
	}

	const queryString = queryParts.join("&");
	const results = await http.get(`/cycles?${queryString}`);

	return {
		docs: results.data.data,
		pages: results.data.pages,
		total: results.data.total,
		currentPage: results.data.currentPage,
	};
}

export function getStatusText(status: number): string {
	switch (status) {
		case 10:
			return "Chờ Station";
		case 20:
			return "Đang tranh chấp";
		case 30:
			return "Đã xác nhận";
		case 40:
			return "Đã thanh toán";
		default:
			return "Không xác định";
	}
}

export function getStatusColor(status: number): string {
	switch (status) {
		case 10:
			return "#F04438"; // Đỏ - Chờ xác nhận
		case 20:
			return "#F59E0B"; // Cam - Tranh chấp
		case 30:
			return "#10B981"; // Xanh lá - Đã xác nhận
		case 40:
			return "#059669"; // Xanh đậm - Đã thanh toán
		default:
			return "#6B7280"; // Xám - Không xác định
	}
}

export function formatPeriod(month: number, year: number): string {
	return `${month}/${year}`;
}

export function formatCurrency(amount: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
	}).format(amount);
}

export function formatNumber(amount: number): string {
	return new Intl.NumberFormat("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
}

export interface CyclesCounters {
	waitingStation: {
		status: number;
		cycleCount: string;
		totalIncome: number;
	};
	watingAdmin: {
		cycleCount: number;
		adjustmentCount: number;
		bookingCount: number;
	};
	disputed: {
		cycleCount: number;
		totalIncome: number;
	};
	waitingPayment: {
		cycleCount: number;
		totalIncome: number;
	};
}

export async function getCyclesCounters(): Promise<CyclesCounters> {
	const results = await http.get("/cycles/counters");
	return results.data;
}

export interface SettlementCycleDetail {
	id: number;
	partnerId: number;
	code: string;
	periodMonth: number;
	periodYear: number;
	status: number;
	startDate: string;
	endDate: string;
	totalIncome: number;
	country?: string;
	currency?: string;
	sentAt: string | null;
	closeAt: string | null;
	paidAt: string | null;
	itemCount: string | number;
	totalQuantity: string | number;
	partner: {
		id: number;
		name: string;
	};
}

export async function getSettlementCycleDetail(cycleId: number): Promise<SettlementCycleDetail> {
	const results = await http.get(`/cycles/${cycleId}`);
	return results.data;
}

export interface SettlementItem {
	id: number;
	orderId: number;
	cycleId: number;
	customerUserId: number;
	customerName: string;
	customerEmail: string;
	incomeAmount: number;
	taxAmount: number;
	adjustmentAmount: number;
	finalAmount: number;
	type: number;
	status: number;
	timeIn: string;
	timeOut: string;
	quantity: number;
	createdAt: string;
	updatedAt: string;
}

export interface SettlementItemsResponse {
	pages: number;
	total: number;
	currentPage: number;
	data: SettlementItem[];
}

export interface SettlementItemsSort {
	id?: "ASC" | "DESC";
	orderId?: "ASC" | "DESC";
	timeIn?: "ASC" | "DESC";
	timeOut?: "ASC" | "DESC";
	type?: "ASC" | "DESC";
	status?: "ASC" | "DESC";
	finalAmount?: "ASC" | "DESC";
	quantity?: "ASC" | "DESC";
}

export async function getSettlementItems(
	page: number = 1,
	perPage: number = 10,
	cycleId?: number,
	orderId?: number,
	type?: number,
	status?: number,
	search?: string,
	sort?: SettlementItemsSort
): Promise<SettlementItemsResponse> {
	const params: any = {
		page: page,
		perPage: perPage,
	};

	if (cycleId) params.cycleId = cycleId;
	if (orderId) params.orderId = orderId;
	if (type !== null && type !== undefined) params.type = type;
	if (status !== null && status !== undefined) params.status = status;
	if (search) params.search = search;

	const queryParts: string[] = [];

	// Add regular params
	Object.entries(params).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			queryParts.push(`${key}=${encodeURIComponent(String(value))}`);
		}
	});

	// Add sort params in format sort[field]=value
	if (sort) {
		Object.entries(sort).forEach(([field, order]) => {
			if (order) {
				queryParts.push(`sort[${field}]=${order}`);
			}
		});
	}

	const queryString = queryParts.join("&");
	const results = await http.get(`/cycles-items?${queryString}`);

	return results.data;
}

export function getItemTypeText(type: number): string {
	switch (type) {
		case 10:
			return "Base";
		case 20:
			return "Adjustment";
		default:
			return "Unknown";
	}
}

export function getItemStatusText(status: number): string {
	switch (status) {
		case 10:
			return "Chờ xử lý";
		case 20:
			return "Chờ duyệt";
		case 30:
			return "Đã duyệt";
		case 40:
			return "Đã từ chối";
		default:
			return "Không xác định";
	}
}

export function getItemStatusColor(status: number): { bg: string; text: string } {
	switch (status) {
		case 10:
			return { bg: "#FEF3C7", text: "#B45309" }; // Cycle Pending
		case 20:
			return { bg: "#FEF3C7", text: "#B45309" }; // Pending
		case 30:
			return { bg: "#ECFDF3", text: "#059669" }; // Confirmed
		case 40:
			return { bg: "#FEF3F2", text: "#B42318" }; // Reject - Đã từ chối
		default:
			return { bg: "#E5E7EB", text: "#6B7280" }; // Unknown
	}
}

export interface CreateAdjustmentData {
	createdBy: number;
	proposedBy: number;
	type: number;
	amount: number;
	reasonCode: string;
	description?: string;
	attachments?: string[];
}

export async function createSettlementAdjustment(cycleId: number, data: CreateAdjustmentData): Promise<any> {
	const results = await http.post(`/cycles/${cycleId}/adjustments`, data);
	return results.data;
}

export async function uploadFiles(files: File[]): Promise<string[]> {
	if (!files || files.length === 0) {
		return [];
	}

	const formData = new FormData();
	files.forEach((file) => {
		formData.append("files", file);
	});

	const results = await http.post("/gcs-upload-multiple-file?folder=documents/settlements", formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});

	return results.data.map((item: any) => item.path || item.url || item.filename).filter(Boolean);
}

export interface SettlementAdjustment {
	id: number;
	orderId?: number | null;
	cycleId: number;
	proposedBy: number;
	type: number;
	amount: number;
	reasonCode: string | null;
	description: string | null;
	attachments: string[] | null;
	status: number;
	createdBy: number;
	reviewedBy: number | null;
	reasonRefusal: string | null;
	createdAt: string;
	updatedAt: string;
	proposer?: {
		id: number;
		role: number;
		fullName?: string;
	};
	reviewer?: {
		id: number;
		role: number;
		fullName?: string;
	};
}

export interface SettlementAdjustmentsResponse {
	pages: number;
	total: number;
	currentPage: number;
	data: SettlementAdjustment[];
}

export interface SettlementAdjustmentsSort {
	id?: "ASC" | "DESC";
	amount?: "ASC" | "DESC";
	createdAt?: "ASC" | "DESC";
	status?: "ASC" | "DESC";
}

export async function getSettlementAdjustments(
	page: number = 1,
	perPage: number = 10,
	cycleId?: number,
	status?: number,
	reasonCode?: string,
	sort?: SettlementAdjustmentsSort
): Promise<SettlementAdjustmentsResponse> {
	const params: any = {
		page: page,
		perPage: perPage,
	};

	if (cycleId) params.cycleId = cycleId;
	if (status !== null && status !== undefined) params.status = status;
	if (reasonCode) params.reasonCode = reasonCode;

	const queryParts: string[] = [];

	// Add regular params
	Object.entries(params).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			queryParts.push(`${key}=${encodeURIComponent(String(value))}`);
		}
	});

	// Add sort params in format sort[field]=value
	if (sort) {
		Object.entries(sort).forEach(([field, order]) => {
			if (order) {
				queryParts.push(`sort[${field}]=${order}`);
			}
		});
	}

	const queryString = queryParts.join("&");
	const results = await http.get(`/cycles-adjustments?${queryString}`);

	return results.data;
}

// Settlement Adjustment Actions
export async function rejectAdjustment(adjustmentId: number, reason: string): Promise<any> {
	const results = await http.put(`/cycles-adjustments/reject/${adjustmentId}`, { reason });
	return results.data;
}

export async function cancelAdjustment(adjustmentId: number, reason: string): Promise<any> {
	const results = await http.put(`/cycles-adjustments/cancel/${adjustmentId}`, { reason });
	return results.data;
}

export async function confirmAdjustment(adjustmentId: number): Promise<any> {
	const results = await http.put(`/cycles-adjustments/confirm/${adjustmentId}`, {});
	return results.data;
}

export async function getAdjustmentDetail(adjustmentId: number): Promise<SettlementAdjustment> {
	const results = await http.get(`/cycles-adjustments/${adjustmentId}`);
	return results.data.adjustment;
}

export async function exportCyclesExcel(
	search?: string,
	status?: number,
	destinationId?: string,
	periodMonth?: number,
	periodYear?: number,
	partnerId?: number,
	sort?: {
		period?: "ASC" | "DESC";
		status?: "ASC" | "DESC";
		bookingCount?: "ASC" | "DESC";
		totalIncome?: "ASC" | "DESC";
	}
): Promise<void> {
	const params: any = {};

	if (search) params.search = search;
	if (status !== null && status !== undefined) params.status = status;
	if (destinationId) params.destinationId = destinationId;
	if (periodMonth) params.periodMonth = periodMonth;
	if (periodYear) params.periodYear = periodYear;
	if (partnerId) params.partnerId = partnerId;

	const queryParts: string[] = [];

	Object.entries(params).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			queryParts.push(`${key}=${encodeURIComponent(String(value))}`);
		}
	});

	if (sort) {
		Object.entries(sort).forEach(([field, order]) => {
			if (order) {
				queryParts.push(`sort[${field}]=${order}`);
			}
		});
	}

	const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";

	const response = await http.get(`/cycles/export-excel${queryString}`, {
		responseType: "blob",
	});

	// Create download link
	const url = window.URL.createObjectURL(new Blob([response.data]));
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", "cycles.xlsx");
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
}

// Create Booking
export interface CreateBookingData {
	customerEmail: string;
	customerName: string;
	amount: number;
	timeIn: string;
	timeOut: string;
	quantity: number;
}

export async function createBooking(cycleId: number, data: CreateBookingData): Promise<any> {
	const results = await http.post(`/cycles/${cycleId}/booking`, data);
	return results.data;
}

export async function confirmBooking(bookingId: number): Promise<any> {
	const results = await http.put(`/cycles-booking/confirm/${bookingId}`, {});
	return results.data;
}

export async function rejectBooking(bookingId: number, reason: string): Promise<any> {
	const results = await http.put(`/cycles-booking/reject/${bookingId}`, { reason });
	return results.data;
}

// Settlement Cycle Actions
export async function confirmCycle(cycleId: number | number[]): Promise<any> {
	const ids = Array.isArray(cycleId) ? cycleId : [cycleId];
	const results = await http.put(`/cycles/confirm`, { id: ids });
	return results.data;
}

export async function rejectCycle(cycleId: number | number[], reason: string): Promise<any> {
	const ids = Array.isArray(cycleId) ? cycleId : [cycleId];
	const results = await http.put(`/cycles/reject`, { id: ids, reason });
	return results.data;
}

export async function cancelCycle(cycleId: number | number[]): Promise<any> {
	const ids = Array.isArray(cycleId) ? cycleId : [cycleId];
	const results = await http.put(`/cycles/cancel`, { id: ids });
	return results.data;
}

export async function getCycleByToken(token: string): Promise<SettlementCycleDetail> {
	const results = await http.post(`/cycles-by-token/${token}`);
	return results.data;
}
